import React, { ReactNode, useMemo } from 'react';
import {
  Handle,
  Position,
  NodeProps,
  useUpdateNodeInternals,
} from 'react-flow-renderer';

import { ClockCircleOutlined } from '@ant-design/icons';

import WidgetModel, { DelayWidgetConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';

import { useSelector } from 'react-redux';
import { StoreState } from '@mm/state/store-interfaces';
import { Date } from '../elements/Date/Date';

import { formatTimePeriod } from '../../../../../../../../../common/widgets/DelayWidget/TimePeriodSelector/TimePeriodSelector';
import { useCommonWidgetHandlers } from '../../../../../../../../../common/helpers/widget-helpers/use-common-widget-handlers';
import { ITime } from '../../../../../../../../components/SlideoutMenu/menus/SlideoutDateTime/SlideoutDateTime';
import { formatDateTime } from '../../../../../../../../../common/helpers/formatDateTime';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';
import { ReviewBadge } from '../elements/ReviewBadge/ReviewBadge';
import { useAutomationActions } from '@mm/state/actions/automationActions';

type Props = { className?: string } & NodeProps<{ widget: WidgetModel }>;

export const DelayIcon = ClockCircleOutlined;

export const Delay: React.FC<Props> = ({
  className,
  children,
  data,
  isDragging,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;

  const config = widget?.config as any as DelayWidgetConfig;

  const commonWidgetHandlers = useCommonWidgetHandlers();
  const viewActions = useViewActions();
  const automationActions = useAutomationActions();

  const updateNodeInternals = useUpdateNodeInternals();

  const placeholder = config.tillTime ? 'Wait until' : 'Wait for';

  const time: ITime = useMemo(() => {
    return {
      date: config.tillTime,
      value: widget.config.value,
      measure: widget.config.measure,
    };
  }, [config.tillTime, widget.config.value, widget.config.measure]);

  const onSubmit = async ({ value, measure, date }: ITime, closeModal) => {
    console.debug(`Delay: onSubmit: `, { value, measure, date });
    try {
      await commonWidgetHandlers.update({
        ...widget,
        config: {
          ...widget.config,
          tillTime: date,
          value,
          measure,
        } as any,
      });
      updateNodeInternals('');
      closeModal();
    } catch (err) {}
  };

  const toggleReviewStatus = async () => {
    if (widget?.needsReview) {
      try {
        await commonWidgetHandlers.updateField(widget, 'needsReview', false);
        automationActions.decreaseReviewRequiredCount();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const onDateClick = () => {
    if (isDragging) {
      return;
    }
    toggleReviewStatus();

    console.debug(`Delay: onDateClick: `, { data });
    viewActions.showSlideoutMenu('dateTime', {
      title: 'Wait',
      onSubmit,
      time: {
        date: config.tillTime,
        value: config.value,
        measure: config.measure,
      },
    });
  };

  useOpenOnReady(widget, onDateClick);

  return (
    <>
      {widget.needsReview && (
        <ReviewBadge onClick={onDateClick} withIcon text="Needs review" />
      )}
      <Handle type="target" position={Position.Top} />
      <Date
        time={time}
        placeholder={placeholder}
        awaitingCount={data.widget?.config?.delaysCount}
        onClick={onDateClick}
      />
      <div>
        {React.Children.map<ReactNode, ReactNode>(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              // @ts-ignore onEdit does not seemto be present or types are off in @types/react
              onEdit: onDateClick,
              key: uniqueId(),
            });
          }
        })}
      </div>
      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
